import { useEffect, useState } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { fetchShopReviews } from "../../api/shops";
import "./ShopReviews.css";

/* "What Our Customers Say" on a shop page (owner's brief, 2026-10-19).

   Every review here comes from a customer whose order with this shop was
   released AND confirmed received by the customer. The server applies that
   rule (backend app/shop_hero.py) and sends names already shortened to
   "First L.", newest first. The header's rating (ShopHero.jsx) is the
   aggregate of the same query, so the two always agree. Nothing here is
   ever seeded or made up: a shop with no such review says so.

   Reviews sit in a staggered (masonry) layout: 4 columns on desktop, 2 on
   tablets, 1 on phones. Reviews are dealt across the columns left to right,
   so the newest are along the top row. */

export const REVIEWS_ANCHOR_ID = "shop-reviews";
export const REVIEWS_STEP = 8;            // shown at first, and added by each "Show more"
export const DESKTOP_QUERY = "(min-width: 1024px)";
export const TABLET_QUERY = "(min-width: 640px)";

/** "Wanjiru K." -> "WK"; "Brian" -> "B". */
export function initials(name) {
  const parts = String(name || "").replace(/\./g, "").split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

/** "2026-10-01T10:00:00Z" -> "Oct 2026". */
export function monthYear(iso) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Date(t).toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
}

/** Deal items across n columns, left to right, so reading order is kept along each row. */
export function dealColumns(items, n) {
  const cols = Array.from({ length: Math.max(1, n) }, () => []);
  items.forEach((item, i) => cols[i % cols.length].push(item));
  return cols;
}

/** Five stars to the nearest half: 4.7 -> 4.5 drawn, 4.8 -> 5. */
export function starSlots(rating) {
  const halves = Math.max(0, Math.min(10, Math.round(Number(rating || 0) * 2)));
  return Array.from({ length: 5 }, (_, i) => (halves >= (i + 1) * 2 ? "full" : halves === i * 2 + 1 ? "half" : "off"));
}

export function Stars({ rating, className = "" }) {
  const value = Number(rating || 0);
  const label = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return (
    <span className={`sr-stars ${className}`} role="img" aria-label={`${label} out of 5 stars`}>
      {starSlots(value).map((kind, i) => (
        <span key={i} className={`sr-star sr-star--${kind}`} aria-hidden="true">★</span>
      ))}
    </span>
  );
}

export function ReviewCard({ review }) {
  const meta = ["Verified purchase", review.product_name].filter(Boolean).join(" · ");
  const date = monthYear(review.created_at);
  return (
    <article className="sr-card">
      <div className="sr-card-top">
        <Stars rating={review.rating} />
        {date && <time className="sr-date" dateTime={review.created_at}>{date}</time>}
      </div>
      {review.body && <p className="sr-body">{review.body}</p>}
      <div className="sr-who">
        <span className="sr-avatar" aria-hidden="true">{initials(review.reviewer)}</span>
        <span className="sr-who-text">
          <span className="sr-name">{review.reviewer}</span>
          <span className="sr-meta">{meta}</span>
        </span>
      </div>
    </article>
  );
}

function SectionHead() {
  return (
    <header className="sr-head">
      <span className="sr-pill">Reviews</span>
      <h2 id={`${REVIEWS_ANCHOR_ID}-title`} className="sr-title" tabIndex={-1}>What Our Customers Say</h2>
    </header>
  );
}

export default function ShopReviews({ shop }) {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const tablet = useMediaQuery(TABLET_QUERY);
  const columns = desktop ? 4 : tablet ? 2 : 1;
  const count = shop.reviews?.count || 0;

  const [items, setItems] = useState(null);
  const [next, setNext] = useState(null);
  const [visible, setVisible] = useState(REVIEWS_STEP);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!count) return undefined;
    let live = true;
    setItems(null);
    setVisible(REVIEWS_STEP);
    setError("");
    fetchShopReviews(shop.handle)
      .then((page) => { if (live) { setItems(page.items || []); setNext(page.next_offset ?? null); } })
      .catch(() => { if (live) { setItems([]); setError("We couldn't load the reviews. Please try again."); } });
    return () => { live = false; };
  }, [shop.handle, count]);

  const showMore = () => {
    const want = visible + REVIEWS_STEP;
    setVisible(want);
    if (want > (items || []).length && next != null && !loading) {
      setLoading(true);
      fetchShopReviews(shop.handle, next)
        .then((page) => { setItems((prev) => [...(prev || []), ...(page.items || [])]); setNext(page.next_offset ?? null); })
        .catch(() => setError("We couldn't load more reviews."))
        .finally(() => setLoading(false));
    }
  };

  const shown = (items || []).slice(0, visible);
  const hasMore = items !== null && (visible < items.length || next != null);

  return (
    <section id={REVIEWS_ANCHOR_ID} className="sr" aria-labelledby={`${REVIEWS_ANCHOR_ID}-title`}>
      <SectionHead />
      {!count ? (
        <div className="sr-card sr-empty">
          <p>No reviews yet. Reviews come only from customers who've received their order.</p>
        </div>
      ) : (
        <>
          {error && <p className="sr-error" role="alert">{error}</p>}
          {items === null && <p className="sr-loading">Loading reviews…</p>}
          {shown.length > 0 && (
            <div className={`sr-masonry${hasMore ? " sr-masonry--more" : ""}`} data-columns={columns}>
              {dealColumns(shown, columns).map((col, c) => (
                <div className="sr-col" key={c}>
                  {col.map((r, i) => <ReviewCard key={`${r.created_at}-${c}-${i}`} review={r} />)}
                </div>
              ))}
            </div>
          )}
          {hasMore && (
            <div className="sr-more">
              <button type="button" className="sr-more-btn" onClick={showMore} disabled={loading}>
                {loading ? "Loading…" : "Show more"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
